import { isDesktop } from '@carboncopyco/mimesis-shared';
import type { TourAspectRatio } from '@carboncopyco/types';
import { type RefObject, useCallback, useEffect } from 'react';

type Size = {
  width: number;
  height: number;
};

type useScaleDownToWindowProps = {
  targetElement: HTMLElement | undefined;
  initialSize: Size;
  isEditor?: boolean;
  enabled?: boolean;
  aspectRatio: TourAspectRatio;
  containerRef: RefObject<HTMLElement>;
  playerIframe: HTMLElement;
  isAnyFrameScreenshot: boolean;
  replayerWrapper?: HTMLElement;
  handleDesktopResize?: () => void;
  isCaptureMode?: boolean;
  activeTabId?: number;
  isSpotlightActive?: boolean;
};

const scaleDownToFit = (current: number, base: number): number => {
  const scale = Number.parseFloat((current / base).toFixed(2));

  return scale < 1 ? scale : 1;
};

const roundToNearest0_05 = (number) => {
  return Math.floor(number * 20) / 20;
};

export const useScaleDownToWindow = ({
  targetElement,
  initialSize,
  aspectRatio,
  containerRef,
  playerIframe,
  isAnyFrameScreenshot,
  replayerWrapper,
  handleDesktopResize,
  isCaptureMode = false,
  activeTabId,
  isSpotlightActive = false,
}: useScaleDownToWindowProps) => {
  useEffect(() => {
    if (replayerWrapper) {
      replayerWrapper.setAttribute('style', 'width: 100%; height: 100%;');
    }
  }, [replayerWrapper, aspectRatio]);

  const setResponsiveSizeAndScale = useCallback(() => {
    playerIframe.setAttribute('scrolling', 'yes');
    playerIframe.setAttribute(
      'sandbox',
      'allow-same-origin allow-scripts allow-forms'
    );
    playerIframe.setAttribute('style', 'width: 100%; height: 100%;');

    targetElement.removeAttribute('style');
  }, [playerIframe, targetElement]);

  const setFixedWidthAndHeightSizeAndScale = useCallback(() => {
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    if (containerWidth === 0 || containerHeight === 0) {
      return;
    }

    const scaleX = scaleDownToFit(containerWidth, initialSize.width);
    const scaleY = scaleDownToFit(containerHeight, initialSize.height);
    const scale = Math.min(scaleX, scaleY);
    let { width, height } = initialSize;

    let transform = scale;

    if (
      isAnyFrameScreenshot &&
      initialSize.width === containerWidth &&
      initialSize.height > containerHeight
    ) {
      /**
       * Little trick to support scalling image frames which are captured in desktop, but opened in web.
       * This is done because when ss is captured in desktop and opened in web, it will have bigger height then container
       * and we would need to scale height as well. To avaoid that, we will use containerHeght and not transform anything
       */

      transform = 1;
      height = containerHeight;
      width = containerWidth;
    }

    // We are rounding numbers to 0.5, 0.55, 0.6, 0.65 etc.
    const transformAdjusted = roundToNearest0_05(transform);

    // We need to scale wrapper container
    // Since scalling + mix-blend-mode for spotlight can cause blurriness, we are adding will-change property.
    // It's not that much recomanded because of performance, so we are applying only if we have spotlight enabled and it's scalling
    targetElement.setAttribute(
      'style',
      `width: ${width}px !important; height: ${height}px !important; transform: scale(${transformAdjusted})
      !important; flex-shrink: 0;  ${isSpotlightActive && transformAdjusted !== 1 ? 'will-change: transform;' : ''}`
    );

    playerIframe?.setAttribute('width', width.toString());
    playerIframe?.setAttribute('height', height.toString());
    playerIframe?.parentElement?.setAttribute(
      'style',
      'display:flex; justify-content: center; align-items: center;'
    );
  }, [
    initialSize.height,
    initialSize.width,
    containerRef,
    isAnyFrameScreenshot,
    targetElement,
    playerIframe,
    isSpotlightActive,
  ]);

  const setWebElementSizeAndScale = useCallback(() => {
    if (!targetElement || !containerRef.current || !playerIframe) {
      return;
    }

    if (aspectRatio === 'responsive') {
      setResponsiveSizeAndScale();
      return;
    }

    if (aspectRatio === 'fixedWidthAndHeight') {
      setFixedWidthAndHeightSizeAndScale();
    }
  }, [
    targetElement,
    containerRef,
    playerIframe,
    aspectRatio,
    setResponsiveSizeAndScale,
    setFixedWidthAndHeightSizeAndScale,
  ]);

  const handleResize = useCallback(() => {
    if (isDesktop && isCaptureMode) {
      handleDesktopResize?.();
      return;
    }

    setWebElementSizeAndScale();
  }, [handleDesktopResize, setWebElementSizeAndScale, isCaptureMode]);

  useEffect(() => {
    if (initialSize.height && initialSize.width) {
      handleResize();
      window.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [
    initialSize.height,
    initialSize.width,
    handleResize,
    activeTabId,
    playerIframe?.clientHeight,
    playerIframe?.clientWidth,
  ]);
};
